import './styles/DefaultErrorTemplate.scss';

import { useMemo } from 'react';

import { cn } from '../../../../helpers/classBuilders';
import { useClassHelper } from '../../../../hooks';
import { IconRenderer } from '../../../IconRenderer';
import { TextEditor } from '../../../TextEditor';
import { Button } from '../Button';

interface DefaultErrorTemplatePropsTypes {
  baseComponentName?: string;
  closeButtonText?: string;
  title?: string;
  errorCode: string;
  localisation: any;
  style?: Record<string, string | number>;
  onClick?: () => void;
  isPortalPreview?: boolean;
  // Linked (Family Plan) mode: prefer the `family_plan_` variant of the
  // resolved localisation key, falling back to the paired copy.
  isLinked?: boolean;
  'data-dd-action-name'?: string;
}

const FE_ERROR_CODES = {
  LOCALIZATION_RENABLE_CELLULAR_ON_EXISTING_PLAN_NOT_SUPPORTED:
    'localization_renable_cellular_on_existing_plan_not_supported',
  LOCALIZATION_PROVISIONING_ERROR: 'localization_provisioning_error',
  LOCALIZATION_AUTHENTICATION_ERROR: 'localization_authentication_error',
  LOCALIZATION_GENERIC_ERROR: 'localization_generic_error',
  MULTI_SIM_PLAN_NOT_ELIGIBLE: 'multi_sim_plan_not_eligible',
  MAX_SUPPORTED_SECONDARY_DEVICES_EXCEEDED:
    'max_supported_secondary_devices_exceeded',
};

const ErrorsKeyMap = {
  MULTI_SIM_PLAN_NOT_ELIGIBLE: FE_ERROR_CODES.MULTI_SIM_PLAN_NOT_ELIGIBLE,
  // Linked (Family Plan) sibling of MULTI_SIM_PLAN_NOT_ELIGIBLE: same
  // eligibility copy field; the isLinked pass picks the family_plan_ twin.
  SA_WATCH_PLAN_NOT_ELIGIBLE: FE_ERROR_CODES.MULTI_SIM_PLAN_NOT_ELIGIBLE,
  MAX_SUPPORTED_SECONDARY_DEVICES_EXCEEDED:
    FE_ERROR_CODES.MAX_SUPPORTED_SECONDARY_DEVICES_EXCEEDED,
  WATCH_CONFIGURATION_NOT_FOUND: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  INVALID_PLAN_STATUS: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  INVALID_PLAN_OFFER_ID: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  COULD_NOT_RETRIEVE_SIM_STATUS_INFO: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  COULD_NOT_RETRIEVE_PLANS: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  SESSION_NOT_FOUND: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  INVALID_STATE: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  // Watch Management (manage) has no dedicated Error States fields at all
  // (matrix POST /watch/manage — "no error fields on this step"); all 9
  // manage-only codes fall through to the generic error, this being the
  // one with no closer existing mapping.
  INVALID_DEVICE_MANAGEMENT_ACTION: FE_ERROR_CODES.LOCALIZATION_GENERIC_ERROR,
  AUTHENTICATION_ERROR: FE_ERROR_CODES.LOCALIZATION_AUTHENTICATION_ERROR,
  COMPANION_DEVICE_NOT_FOUND: FE_ERROR_CODES.LOCALIZATION_PROVISIONING_ERROR,
  DEVICE_MANAGEMENT_FAILED: FE_ERROR_CODES.LOCALIZATION_PROVISIONING_ERROR,
  DEVICE_PAIRING_FAILED: FE_ERROR_CODES.LOCALIZATION_PROVISIONING_ERROR,
  RENABLE_CELLULAR_ON_EXISTING_PLAN_NOT_SUPPORTED:
    FE_ERROR_CODES.LOCALIZATION_RENABLE_CELLULAR_ON_EXISTING_PLAN_NOT_SUPPORTED,
};

const getErrorKey = (errorCode: string) => {
  return ErrorsKeyMap[errorCode] || 'default_error_message';
};

const componentName = 'c-DefaultErrorTemplate';

export const DefaultErrorTemplate = ({
  baseComponentName,
  closeButtonText = 'Close',
  errorCode,
  localisation,
  title,
  style,
  onClick = () => null,
  isPortalPreview,
  isLinked = false,
  'data-dd-action-name': dataDdActionName,
}: DefaultErrorTemplatePropsTypes) => {
  const errorMessage = useMemo(() => {
    if (isPortalPreview && localisation) {
      return (
        localisation[errorCode] || localisation?.['localization_generic_error']
      );
    }

    const errorKey = getErrorKey(errorCode);

    return (
      (isLinked ? localisation?.[`family_plan_${errorKey}`] : undefined) ||
      localisation?.[errorKey] ||
      (isLinked
        ? localisation?.['family_plan_localization_generic_error']
        : undefined) ||
      localisation?.['localization_generic_error']
    );
  }, [localisation, errorCode, isPortalPreview, isLinked]);

  const baseName = baseComponentName || 'c-DefaultErrorTemplate';

  const c = useClassHelper(baseName);

  return (
    <main className={componentName} style={style}>
      <section
        className={cn(c('__status'), 'flex-1 shrink basis-0 pt-5 pb-6 px-4', {
          'pb-9': isPortalPreview,
        })}
      >
        <div className={c('__title', '__title--error')}>
          {title || 'Something went wrong'}
        </div>
        <figure className={c('__status__icon')}>
          <IconRenderer variant="error" />
        </figure>
        <div className={c('__paragraph')}>
          {Array.isArray(errorMessage) ? (
            <TextEditor viewOnly value={errorMessage} />
          ) : (
            errorMessage
          )}
        </div>
        <Button
          type="button"
          variants="primary"
          onClick={onClick}
          data-dd-action-name={dataDdActionName}
        >
          {closeButtonText}
        </Button>
      </section>
    </main>
  );
};
